import React from "react";
import { ArrowRight, Sparkles, Code2, Smartphone, Database, Cloud } from "lucide-react";

const techStack = [
  ".NET / .NET Core", "Angular", "React JS", "React Native",
  "SQL Server", "PostgreSQL", "Firebase", "REST APIs",
  "Cloud Hosting", "CI/CD", "Tailwind", "Node.js",
];

export const Header = () => {
  return (
    <header id="header" className="relative overflow-hidden bg-[#05070f]">
      {/* Background layers */}
      <div className="absolute inset-0">
        <div className="aurora w-[520px] h-[520px] -top-24 -left-24 bg-blue-600/60" />
        <div className="aurora w-[560px] h-[560px] top-10 right-[-140px] bg-violet-600/50" style={{ animationDelay: "-6s" }} />
        <div className="aurora w-[420px] h-[420px] bottom-[-120px] left-1/3 bg-cyan-500/40" style={{ animationDelay: "-11s" }} />
        <div className="absolute inset-0 grid-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05070f]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          {/* LEFT — copy */}
          <div className="text-center lg:text-left">
            <div className="reveal inline-flex">
              <span className="eyebrow">
                <Sparkles className="h-3.5 w-3.5" /> India-based Software Studio
              </span>
            </div>

            <h1 className="reveal d1 mt-6 font-display text-white text-[42px] leading-[1.05] sm:text-[56px] md:text-[68px]">
              We <span className="text-gradient">design, build</span> &amp;
              <br className="hidden sm:block" /> ship digital products
            </h1>

            <p className="reveal d2 mt-6 text-slate-300/90 text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              Sudeeshaa Solutions builds scalable web &amp; mobile applications with
              modern stacks — <span className="text-white font-semibold">.NET, Angular, React, React Native</span> —
              backed by robust APIs, databases, hosting &amp; long-term support.
            </p>

            <div className="reveal d3 mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-extrabold text-white bg-gradient-to-br from-blue-600 to-violet-600 btn-glow hover:-translate-y-0.5 transition"
              >
                Start a Project <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="#portfolio"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-extrabold text-white glass-strong hover:bg-white/10 transition"
              >
                View Our Work
              </a>
            </div>

            {/* mini stats */}
            <div className="reveal d4 mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
              {[
                ["6+", "Products Shipped"],
                ["100%", "On-time Delivery"],
                ["24/7", "Support"],
              ].map(([n, l]) => (
                <div key={l} className="text-center lg:text-left">
                  <div className="font-display text-2xl md:text-3xl text-white">{n}</div>
                  <div className="text-xs md:text-sm text-slate-400 font-semibold mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — floating glass cards */}
          <div className="reveal d3 relative h-[360px] hidden sm:block">
            <div className="absolute inset-0 rounded-[2rem] glass-strong overflow-hidden">
              <div className="absolute inset-0 grid-overlay opacity-60" />
              <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-16 -left-10 w-52 h-52 rounded-full bg-violet-500/30 blur-3xl" />
            </div>

            <FloatCard className="left-5 top-6 float" icon={Code2} title="Web Apps" sub="Angular · React · .NET" color="from-blue-500 to-cyan-400" />
            <FloatCard className="right-4 top-24 float-slow" icon={Smartphone} title="Mobile Apps" sub="React Native" color="from-violet-500 to-fuchsia-400" />
            <FloatCard className="left-8 bottom-10 float-slow" icon={Database} title="Databases" sub="SQL · Postgres · Firebase" color="from-emerald-500 to-teal-400" />
            <FloatCard className="right-8 bottom-4 float" icon={Cloud} title="Deploy & Host" sub="DNS · SSL · Cloud" color="from-amber-500 to-orange-400" />
          </div>
        </div>
      </div>

      {/* Tech marquee */}
      <div className="relative z-10 pb-14">
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <div className="hair-line" />
        </div>
        <div className="marquee-mask">
          <div className="marquee-track gap-4">
            {[...techStack, ...techStack].map((t, i) => (
              <span
                key={i}
                className="whitespace-nowrap px-5 py-2 rounded-full text-sm font-bold text-slate-300 glass"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

const FloatCard = ({ className, icon: Icon, title, sub, color }) => (
  <div className={`absolute w-[190px] rounded-2xl glass-strong p-4 shadow-[0_20px_50px_rgba(0,0,0,0.4)] ${className}`}>
    <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${color} grid place-items-center mb-3`}>
      <Icon className="h-5 w-5 text-white" strokeWidth={2.4} />
    </div>
    <div className="text-white font-extrabold text-sm">{title}</div>
    <div className="text-slate-400 text-xs font-semibold mt-0.5">{sub}</div>
  </div>
);
